/**
 * GPU spark/plume particle layer for thruster exhaust.
 * Renders THREE.Points with a custom ShaderMaterial that analytically
 * simulates spark and plume particles entirely on the GPU.
 *
 * Placed as a sibling to the flame mesh inside ThrusterExhaust's group.
 * No CPU per-particle updates — birth times staggered, simulation via modulo.
 *
 * Props: { active, afterburner } — parent group handles rotation (reverse).
 *
 * Subscribes to particleEffectStore to apply spark config as shader uniforms.
 * Rebuilds geometry when sparkCount changes.
 */

import { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import {
  sparkVertexShader,
  sparkFragmentShader,
} from '../shaders/thrusterSparkShaders.js';
import { useParticleEffectStore } from '../../stores/particleEffectStore.js';
import type { ThrusterSparksConfig } from '@warmancer/shared';
import { DEFAULT_THRUSTER_SPARKS } from '@warmancer/shared';

interface ThrusterSparksProps {
  readonly active: boolean;
  readonly afterburner: boolean;
}

/** Lerp speed for intensity activation (units per second). */
const INTENSITY_LERP_SPEED = 12;

/** Lerp speed for afterburner mode transition (units per second). */
const AFTERBURNER_LERP_SPEED = 8;

/** Below this intensity, snap to zero and hide points. */
const INTENSITY_SNAP_THRESHOLD = 0.01;

/** Build BufferGeometry for N spark particles with staggered birth times. */
function buildSparkGeometry(count: number, maxLifetime: number): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  const birthTimes = new Float32Array(count);
  const seeds = new Float32Array(count);

  for (let i = 0; i < count; i++) {
    birthTimes[i] = i * maxLifetime / count;
    seeds[i] = Math.random();
  }

  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(count * 3), 3));
  geo.setAttribute('aBirthTime', new THREE.BufferAttribute(birthTimes, 1));
  geo.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1));

  return geo;
}

export function ThrusterSparks({ active, afterburner }: ThrusterSparksProps) {
  const pointsRef = useRef<THREE.Points>(null);
  const geometryRef = useRef<THREE.BufferGeometry | null>(null);

  // Subscribe to spark config from store for geometry sizing (React render path).
  // draftOverrides has priority (particle editor), then effects (live game), then defaults.
  const sparkConfig = useParticleEffectStore((s) =>
    (s.draftOverrides.thruster_sparks as ThrusterSparksConfig | undefined)
    ?? (s.effects.thruster_sparks as ThrusterSparksConfig | undefined)
    ?? DEFAULT_THRUSTER_SPARKS,
  );

  // Max lifetime for staggering birth times (plume lifetime = longest)
  const maxLifetime = sparkConfig.plumeLifetime;
  const sparkCount = sparkConfig.sparkCount;

  // Stable geometry — rebuilt when sparkCount changes
  const geometry = useMemo(() => {
    const geo = buildSparkGeometry(sparkCount, maxLifetime);
    geometryRef.current = geo;
    return geo;
  }, [sparkCount, maxLifetime]);

  // Dispose geometry on unmount or rebuild
  useEffect(() => {
    return () => {
      geometryRef.current?.dispose();
    };
  }, [geometry]);

  // Stable uniforms object
  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uIntensity: { value: 0 },
      uAfterburner: { value: 0 },
      // Vertex shader configurable uniforms
      uPlumeSeedThreshold: { value: DEFAULT_THRUSTER_SPARKS.plumeSeedThreshold },
      uSparkLifetime: { value: DEFAULT_THRUSTER_SPARKS.sparkLifetime },
      uPlumeLifetime: { value: DEFAULT_THRUSTER_SPARKS.plumeLifetime },
      uSparkSpeed: { value: DEFAULT_THRUSTER_SPARKS.sparkSpeed },
      uPlumeSpeed: { value: DEFAULT_THRUSTER_SPARKS.plumeSpeed },
      uAfterburnerSpeedBoost: { value: DEFAULT_THRUSTER_SPARKS.afterburnerSpeedBoost },
      uConeAngle: { value: DEFAULT_THRUSTER_SPARKS.coneAngle },
      uNozzleHalfWidth: { value: DEFAULT_THRUSTER_SPARKS.nozzleHalfWidth },
      uSparkDrag: { value: DEFAULT_THRUSTER_SPARKS.sparkDrag },
      uPlumeDrag: { value: DEFAULT_THRUSTER_SPARKS.plumeDrag },
      uSparkSize: { value: DEFAULT_THRUSTER_SPARKS.sparkSize },
      uPlumeSize: { value: DEFAULT_THRUSTER_SPARKS.plumeSize },
      // Fragment shader configurable uniforms
      uSparkEdgeSoftness: { value: DEFAULT_THRUSTER_SPARKS.sparkEdgeSoftness },
      uPlumeEdgeSoftness: { value: DEFAULT_THRUSTER_SPARKS.plumeEdgeSoftness },
      uNormalHot: { value: new THREE.Vector3(...DEFAULT_THRUSTER_SPARKS.normalHot) },
      uNormalMid: { value: new THREE.Vector3(...DEFAULT_THRUSTER_SPARKS.normalMid) },
      uNormalCool: { value: new THREE.Vector3(...DEFAULT_THRUSTER_SPARKS.normalCool) },
      uAbHot: { value: new THREE.Vector3(...DEFAULT_THRUSTER_SPARKS.abHot) },
      uAbMid: { value: new THREE.Vector3(...DEFAULT_THRUSTER_SPARKS.abMid) },
      uAbCool: { value: new THREE.Vector3(...DEFAULT_THRUSTER_SPARKS.abCool) },
      uFlickerFreq: { value: DEFAULT_THRUSTER_SPARKS.flickerFreq },
      uFlickerAmp: { value: DEFAULT_THRUSTER_SPARKS.flickerAmp },
      uHdrMultNormal: { value: DEFAULT_THRUSTER_SPARKS.hdrMultNormal },
      uHdrMultAB: { value: DEFAULT_THRUSTER_SPARKS.hdrMultAfterburner },
    }),
    [],
  );

  // Animate uniforms per frame
  useFrame((_, delta) => {
    const points = pointsRef.current;
    if (!points) return;

    // Smooth lerp toward target intensity
    const targetIntensity = active ? 1.0 : 0.0;
    const intensityStep = INTENSITY_LERP_SPEED * delta;
    uniforms.uIntensity.value += (targetIntensity - uniforms.uIntensity.value) * Math.min(1, intensityStep);

    // Snap to zero below threshold
    if (uniforms.uIntensity.value < INTENSITY_SNAP_THRESHOLD && !active) {
      uniforms.uIntensity.value = 0;
    }

    // Hide points when fully inactive (zero GPU cost)
    points.visible = uniforms.uIntensity.value > 0;

    if (!points.visible) return;

    // Smooth lerp toward target afterburner mode
    const targetAfterburner = afterburner ? 1.0 : 0.0;
    const abStep = AFTERBURNER_LERP_SPEED * delta;
    uniforms.uAfterburner.value += (targetAfterburner - uniforms.uAfterburner.value) * Math.min(1, abStep);

    // Advance time
    uniforms.uTime.value += delta;

    // Read config via getState() — bypasses React rendering entirely.
    // draftOverrides is set by the particle editor; effects is the live game config.
    const store = useParticleEffectStore.getState();
    const cfg = (store.draftOverrides.thruster_sparks as ThrusterSparksConfig | undefined)
      ?? (store.effects.thruster_sparks as ThrusterSparksConfig | undefined)
      ?? DEFAULT_THRUSTER_SPARKS;
    uniforms.uPlumeSeedThreshold.value = cfg.plumeSeedThreshold;
    uniforms.uSparkLifetime.value = cfg.sparkLifetime;
    uniforms.uPlumeLifetime.value = cfg.plumeLifetime;
    uniforms.uSparkSpeed.value = cfg.sparkSpeed;
    uniforms.uPlumeSpeed.value = cfg.plumeSpeed;
    uniforms.uAfterburnerSpeedBoost.value = cfg.afterburnerSpeedBoost;
    uniforms.uConeAngle.value = cfg.coneAngle;
    uniforms.uNozzleHalfWidth.value = cfg.nozzleHalfWidth;
    uniforms.uSparkDrag.value = cfg.sparkDrag;
    uniforms.uPlumeDrag.value = cfg.plumeDrag;
    uniforms.uSparkSize.value = cfg.sparkSize;
    uniforms.uPlumeSize.value = cfg.plumeSize;
    uniforms.uSparkEdgeSoftness.value = cfg.sparkEdgeSoftness;
    uniforms.uPlumeEdgeSoftness.value = cfg.plumeEdgeSoftness;
    uniforms.uNormalHot.value.set(...cfg.normalHot);
    uniforms.uNormalMid.value.set(...cfg.normalMid);
    uniforms.uNormalCool.value.set(...cfg.normalCool);
    uniforms.uAbHot.value.set(...cfg.abHot);
    uniforms.uAbMid.value.set(...cfg.abMid);
    uniforms.uAbCool.value.set(...cfg.abCool);
    uniforms.uFlickerFreq.value = cfg.flickerFreq;
    uniforms.uFlickerAmp.value = cfg.flickerAmp;
    uniforms.uHdrMultNormal.value = cfg.hdrMultNormal;
    uniforms.uHdrMultAB.value = cfg.hdrMultAfterburner;
  });

  return (
    <points ref={pointsRef} visible={false}>
      <primitive object={geometry} attach="geometry" />
      <shaderMaterial
        vertexShader={sparkVertexShader}
        fragmentShader={sparkFragmentShader}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}
